/**
 * Team Design — color swatches on product cards.
 *
 * For every card in the collection grid:
 *   1. Parses the SKU to find a product code (and optionally pinned PS/CT params)
 *   2. Calls /api/squad/rest/bundle-design-options/<code> to load the
 *      product's bundle pattern sets + color themes
 *   3. Renders up to 9 color swatches under the card's image (pinned CT first,
 *      then a shuffled sample of the remaining themes)
 *   4. Wires hover + click on each swatch:
 *        - hover  → fetch the pattern SVG, recolor it for the swatch's theme,
 *                   overlay it on the card image
 *        - click  → navigate to the PDP with ?CT=<theme.code>
 *
 * SVG fetch + colorize are delegated to window.TeamDesignSvg so this file
 * shares a single cache with the team-name and team-logo overlay systems.
 *
 * Note: attachImageHover (scrub-the-image color preview) is implemented but
 * its call site is intentionally commented out — kept here so the feature
 * can be toggled back on without re-implementing.
 */
(function () {
  'use strict';

  if (!document.getElementById('cdopt-styles')) {
    var style = document.createElement('style');
    style.id = 'cdopt-styles';
    style.textContent =
      '.card-design-preview{position:absolute;inset:0;display:none;pointer-events:none;overflow:hidden;}' +
      '.card-design-preview svg{width:100%;height:100%;}' +
      '.card-color-swatch--active{box-shadow:0 0 0 2px #fff,0 0 0 3px rgba(0,0,0,0.4)!important;transform:scale(1.12)!important;}';
    document.head.appendChild(style);
  }

  var SvgEngine    = window.TeamDesignSvg;
  var designCache  = new Map(); // per-product API response cache

  // ─── SKU PARSING ────────────────────────────────────────────────────────────
  function parseSku(sku) {
    if (!sku || sku.indexOf(':::') === -1) return { code: sku || '', ps: null, ct: null };
    var sep    = sku.indexOf(':::');
    var code   = sku.slice(0, sep);
    var params = sku.slice(sep + 3);
    var psMatch = params.match(/PS:([^-]+)/);
    var ctMatch = params.match(/CT:([^-]+)/);
    return { code: code, ps: psMatch ? psMatch[1] : null, ct: ctMatch ? ctMatch[1] : null };
  }

  // ─── PATTERN MATCHING ───────────────────────────────────────────────────────
  // Shared with collection-style-filters.js so both scripts pick the exact
  // same "shown" pattern set for a card: the PS-pinned code if present and
  // found, else the bundle's first pattern set.
  function resolveMatchedPattern(patterns, ps) {
    if (!patterns || !patterns.length) return null;
    if (ps !== null) {
      for (var i = 0; i < patterns.length; i++) {
        if (String(patterns[i].code) === String(ps)) return patterns[i];
      }
    }
    return patterns[0];
  }

  // ─── API FETCH ──────────────────────────────────────────────────────────────
  function fetchDesignOptions(code) {
    if (designCache.has(code)) return Promise.resolve(designCache.get(code));
    // Base host + env-correct /api/<apiPath> come from snippets/app-config.liquid.
    var base = (window.APP_CONFIG && window.APP_CONFIG.endpoints && window.APP_CONFIG.endpoints.bundleDesignOptions) ||
               'https://scripts.squadmojo-dev.com/api/squad/rest/bundle-design-options';
    var url = base + '/' + encodeURIComponent(code);
    return fetch(url)
      .then(function (res) { if (!res.ok) throw new Error(); return res.json(); })
      .then(function (d)   { designCache.set(code, d); return d; })
      .catch(function ()   { designCache.set(code, null); return null; });
  }

  // ─── COLOR SIMILARITY (unused — kept for the optional swatch-diversity pass) ─
  var COLOR_SIMILARITY_THRESHOLD = 50; // RGB Euclidean distance (0–441); ~50 ≈ "visually close"

  function parseHexColor(hex) {
    hex = (hex || '').replace('#', '');
    if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
    if (hex.length !== 6) return null;
    return [parseInt(hex.slice(0,2),16), parseInt(hex.slice(2,4),16), parseInt(hex.slice(4,6),16)];
  }

  function colorDistance(hexA, hexB) {
    var a = parseHexColor(hexA), b = parseHexColor(hexB);
    if (!a || !b) return 999;
    return Math.sqrt(Math.pow(a[0]-b[0],2) + Math.pow(a[1]-b[1],2) + Math.pow(a[2]-b[2],2));
  }

  function isTooSimilar(hex, usedList) {
    for (var i = 0; i < usedList.length; i++) {
      if (colorDistance(hex, usedList[i]) < COLOR_SIMILARITY_THRESHOLD) return true;
    }
    return false;
  }

  // ─── COLORED-SVG PRE-COMPUTE ────────────────────────────────────────────────
  // One colored string per theme index — done once on mouseenter, never inside
  // a mousemove or RAF tick.
  function buildColoredSvgs(svgText, themes) {
    var map = new Map();
    for (var i = 0; i < themes.length; i++) {
      map.set(i, SvgEngine.colorize(svgText, themes[i]));
    }
    return map;
  }

  // ─── DOM HELPERS ────────────────────────────────────────────────────────────
  function getOrCreatePreview(card) {
    var el = card.querySelector('.card-design-preview');
    if (el) return el;
    var frame = card.querySelector('.card-product-figma__image-frame');
    if (!frame) return null;
    if (window.getComputedStyle(frame).position === 'static') frame.style.position = 'relative';
    el = document.createElement('div');
    el.className = 'card-design-preview';
    frame.appendChild(el);
    return el;
  }

  function setActiveSwatch(swatches, index) {
    for (var i = 0; i < swatches.length; i++) {
      swatches[i].classList.toggle('card-color-swatch--active', i === index);
    }
  }

  // ─── SWATCH HOVER ───────────────────────────────────────────────────────────
  function attachSwatchHovers(card, swatchesEl, swatches, mediaWrap, previewEl, thumbnail, state) {
    var debounceTimer = null;

    function restore() {
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
      state.activeIndex = -1;
      setActiveSwatch(swatches, -1);

      var teamState     = window.CollectionTeamText && window.CollectionTeamText.state;
      var bannerTheme   = teamState && teamState.bannerTheme;
      var hasData       = teamState && (teamState.currentText || teamState.currentLogoUrl || bannerTheme);

      // If team name, logo, or a banner-picked theme is set, keep the SVG
      // visible and re-render with that theme (banner choice wins; otherwise
      // fall back to swatches[0]). The SVG is already cached from the swatch
      // mouseenter so this is synchronous.
      if (hasData && thumbnail) {
        var cached = SvgEngine.getCached(thumbnail);
        if (cached) {
          var theme;
          if (bannerTheme) {
            theme = {
              primaryColor:   bannerTheme.primaryColor   || '',
              secondaryColor: bannerTheme.secondaryColor || '',
              tertiaryColor:  bannerTheme.tertiaryColor  || '',
              accentColor:    bannerTheme.accentColor    || '',
            };
          } else {
            var defSwatch = swatches[0];
            theme = defSwatch ? {
              primaryColor:   defSwatch.dataset.primary   || '',
              secondaryColor: defSwatch.dataset.secondary || '',
              tertiaryColor:  defSwatch.dataset.tertiary  || '',
              accentColor:    defSwatch.dataset.accent    || '',
            } : { primaryColor: '', secondaryColor: '', tertiaryColor: '', accentColor: '' };
          }
          mediaWrap.style.visibility = 'hidden';
          previewEl.innerHTML        = SvgEngine.colorize(cached, theme);
          previewEl.dataset.autoLoaded = '1';
          if (teamState.currentLogoUrl) previewEl.dataset.logoAutoLoaded = '1';
          previewEl.style.display    = 'block';
          // MutationObserver fires → injectTeamText + injectLogo run automatically.
          return;
        }
      }

      previewEl.style.display = 'none';
      previewEl.innerHTML     = '';
      mediaWrap.style.visibility = '';
    }

    swatches.forEach(function (swatch, index) {
      swatch.addEventListener('mouseenter', function () {
        state.activeIndex = index;
        var theme = {
          primaryColor:   swatch.dataset.primary   || '',
          secondaryColor: swatch.dataset.secondary || '',
          tertiaryColor:  swatch.dataset.tertiary  || '',
          accentColor:    swatch.dataset.accent    || '',
        };

        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          debounceTimer = null;
          if (!thumbnail) return;
          SvgEngine.fetch(thumbnail).then(function (svgText) {
            if (!svgText) return;
            mediaWrap.style.visibility = 'hidden';
            previewEl.innerHTML = SvgEngine.colorize(svgText, theme);
            previewEl.style.display = 'block';
            setActiveSwatch(swatches, index);
          });
        }, 35);
      });

      swatch.addEventListener('click', function (e) {
        e.stopPropagation();
        var code = swatch.dataset.themeCode;
        if (!code) return;
        var productUrl = card.dataset.productUrl;
        if (!productUrl) return;
        var url = new URL(productUrl, window.location.origin);
        url.searchParams.set('CT', code);
        window.location.href = url.toString();
      });
      // swatch mouseleave → no-op; container mouseleave handles restore
    });

    swatchesEl.addEventListener('mouseleave', restore);
  }

  // ─── IMAGE HOVER (currently disabled — see updateCard) ──────────────────────
  function attachImageHover(card, swatches, mediaWrap, previewEl, themes, thumbnail, state) {
    var frame = card.querySelector('.card-product-figma__image-frame');
    if (!frame || !themes.length || !thumbnail) return;

    var preBuilt       = null; // Map<index, coloredSvgString>
    var cursorX        = 0;
    var containerLeft  = 0;
    var containerWidth = 1;
    var lastZoneIndex  = -1;
    var rafId          = null;

    function rafTick() {
      var newZone = Math.max(0, Math.min(
        Math.floor((cursorX / containerWidth) * themes.length),
        themes.length - 1
      ));

      if (newZone !== lastZoneIndex && preBuilt) {
        lastZoneIndex     = newZone;
        state.activeIndex = newZone;
        // Single DOM write: SVG swap + swatch highlight in one block
        previewEl.innerHTML = preBuilt.get(newZone) || '';
        setActiveSwatch(swatches, newZone);
      }

      rafId = requestAnimationFrame(rafTick);
    }

    function startPreview(svgText) {
      if (!svgText) return;
      preBuilt = buildColoredSvgs(svgText, themes);

      // Show immediately using the already-active theme (or zone 0)
      var startIndex = state.activeIndex >= 0 ? state.activeIndex : 0;
      lastZoneIndex = startIndex;
      mediaWrap.style.visibility = 'hidden';
      previewEl.innerHTML = preBuilt.get(startIndex) || '';
      previewEl.style.display = 'block';
      setActiveSwatch(swatches, startIndex);

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(rafTick);
    }

    frame.addEventListener('mouseenter', function () {
      var rect = frame.getBoundingClientRect(); // read layout once here, never in RAF
      containerWidth = rect.width || 1;
      containerLeft  = rect.left;
      cursorX        = 0;

      var cached = SvgEngine.getCached(thumbnail);
      if (cached !== undefined) {
        startPreview(cached);
      } else {
        SvgEngine.fetch(thumbnail).then(startPreview);
      }
    });

    // Passive: only stores cursor position — zero DOM interaction
    frame.addEventListener('mousemove', function (e) {
      cursorX = e.clientX - containerLeft;
    }, { passive: true });

    frame.addEventListener('mouseleave', function () {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      preBuilt      = null;
      lastZoneIndex = -1;
      previewEl.style.display = 'none';
      previewEl.innerHTML     = '';
      mediaWrap.style.visibility = '';
      setActiveSwatch(swatches, -1);
    });
  }

  // ─── CARD UPDATE ────────────────────────────────────────────────────────────
  function updateCard(card, data, parsed) {
    if (!data) return;

    var allThemes = data.bundleColorThemes || [];
    var themes    = [];

    // Pin the CT-matched theme first
    var matchedTheme = null;
    if (parsed.ct) {
      for (var i = 0; i < allThemes.length; i++) {
        if (String(allThemes[i].code) === String(parsed.ct)) {
          matchedTheme = allThemes[i];
          break;
        }
      }
    }
    if (matchedTheme) themes.push(matchedTheme);

    // Sample 9 random themes from the first 32 of the merchant-ordered list.
    // The 32 cap keeps the candidate pool large enough that any single theme
    // (e.g. yellow at position 0) shows only ~9/32 ≈ 28% of the time instead
    // of every render, while still keeping the surfaced colors weighted
    // toward the merchant's front-loaded selection.
    var SHUFFLE_POOL_SIZE = 32;
    var remaining = [];
    for (var i = 0; i < allThemes.length && remaining.length < SHUFFLE_POOL_SIZE; i++) {
      if (matchedTheme && allThemes[i].code === matchedTheme.code) continue;
      remaining.push(allThemes[i]);
    }
    for (var i = remaining.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = remaining[i]; remaining[i] = remaining[j]; remaining[j] = tmp;
    }
    for (var i = 0; i < remaining.length && themes.length < 9; i++) {
      themes.push(remaining[i]);
    }
    var patterns = data.bundlePatternSets;
    if (!themes.length) return;

    var swatchColors = card.querySelector('.card-color-swatches__colors');
    if (!swatchColors) return;

    // Resolve matched pattern
    var matchedPattern = resolveMatchedPattern(patterns, parsed.ps);
    var thumbnail = matchedPattern ? (matchedPattern.thumbnail || '') : '';

    swatchColors.innerHTML = themes.map(function (theme, idx) {
      var primary      = theme.primaryColor || '';
      var displayColor = primary || '#ccc';

      return (
        '<span class="card-color-swatch"' +
        ' style="background-color:' + displayColor + ';"' +
        // ' title="' + (theme.name || '').replace(/"/g, '&quot;') + '"' +
        ' data-thumbnail="'   + thumbnail + '"' +
        ' data-theme-code="'  + (theme.code || '') + '"' +
        ' data-primary="'     + (theme.primaryColor   || '') + '"' +
        ' data-secondary="'   + (theme.secondaryColor || '') + '"' +
        ' data-tertiary="'    + (theme.tertiaryColor  || '') + '"' +
        ' data-accent="'      + (theme.accentColor    || '') + '"' +
        '></span>'
      );
    }).join('') + '<span class="card-color-swatches__count">99+</span>';

    // Shared resources
    var mediaWrap = card.querySelector('.card-product-figma__image-frame .media--transparent');
    var previewEl = getOrCreatePreview(card);
    if (!mediaWrap || !previewEl) return;

    var swatches = Array.from(swatchColors.querySelectorAll('.card-color-swatch'));
    var state    = { activeIndex: -1 };

    attachSwatchHovers(card, swatchColors, swatches, mediaWrap, previewEl, thumbnail, state);
    // attachImageHover(card, swatches, mediaWrap, previewEl, themes, thumbnail, state); // disabled: image-hover color-theme logic temporarily off
  }

  // ─── MAIN INIT ──────────────────────────────────────────────────────────────
  function init() {
    var cards = Array.from(document.querySelectorAll('.card-wrapper[data-sku]:not([data-design-ready])'));
    if (!cards.length) return;

    cards.forEach(function (card) { card.setAttribute('data-design-ready', '1'); });

    var seen = Object.create(null);
    var uniqueCodes = [];
    cards.forEach(function (card) {
      var code = parseSku(card.dataset.sku || '').code;
      if (code && !seen[code]) { seen[code] = true; uniqueCodes.push(code); }
    });

    Promise.all(uniqueCodes.map(fetchDesignOptions)).then(function () {
      cards.forEach(function (card) {
        var parsed = parseSku(card.dataset.sku || '');
        updateCard(card, designCache.get(parsed.code) || null, parsed);
      });
    });
  }

  window.CollectionDesignOptions = {
    init:                 init,
    fetch:                fetchDesignOptions,
    parseSku:             parseSku,
    resolveMatchedPattern: resolveMatchedPattern,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
